'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
// Sticky bar pinned to the top of its scrolling ancestor. When pinned
// (the user has scrolled past the bar's natural flow position), a hairline
// bottom border fades in to separate it from content scrolling behind.
//
// Stuck-state detection observes the bar against its nearest scrolling
// ancestor with `rootMargin: '-1px 0 0 0'`, shrinking the root by 1px from
// the top. Once the bar pins to that root's top, that 1px slice stops
// intersecting, the bar's ratio drops below 1, and the border activates.
// We resolve the scroll ancestor at mount time (rather than relying on the
// viewport) because this app's actual scroll container is AppShell's
// <main>, not the document — observing the viewport never fires "stuck".
//
// Background inherits from the parent so the bar matches whatever surface
// it's on; the immediate parent must declare its own background (otherwise
// inherit resolves to transparent and content scrolls visibly through the
// bar). The bar's own `paddingBlock` is the canonical gap above/below — the
// flex parent should leave gap=0 around StickyBar so spacing isn't
// double-counted.
function findScrollAncestor(node) {
    let cur = node?.parentElement ?? null;
    while (cur) {
        const { overflowY, overflow } = getComputedStyle(cur);
        if (/(auto|scroll|overlay)/.test(overflowY) || /(auto|scroll|overlay)/.test(overflow)) {
            return cur;
        }
        cur = cur.parentElement;
    }
    return null;
}
export function StickyBar({ children }) {
    const ref = useRef(null);
    const [stuck, setStuck] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el)
            return;
        const obs = new IntersectionObserver(([entry]) => {
            if (entry !== undefined)
                setStuck(entry.intersectionRatio < 1);
        }, {
            threshold: [1],
            rootMargin: '-1px 0px 0px 0px',
            root: findScrollAncestor(el),
        });
        obs.observe(el);
        return () => obs.disconnect();
    }, []);
    return (_jsx("div", { ref: ref, style: {
            position: 'sticky',
            top: 0,
            zIndex: 'var(--z-sticky)',
            paddingBlock: 'var(--space-md)',
            background: 'inherit',
            borderBottom: `1px solid ${stuck ? 'var(--border)' : 'transparent'}`,
        }, children: children }));
}
//# sourceMappingURL=StickyBar.js.map